// @ts-check

import { createColumnHelper } from "@tanstack/react-table"
import {
  buildIndividualReportTableCells,
  SelectCellTemplate,
  SelectHeaderTemplate,
} from "./IndividualReportTable.cells"
import { isTaskReportItem } from "../../../../utils/report.utils"
import { tableFilterValueIncludes } from "../../../../components/table/utils/filter.utils"
import { createTaskReportsCommonColumns } from "../../../../factories/reports/createTaskReportsCommonColumns.factory"

/**
 * @typedef {import("../../../../services/reports.service.types").IndividualTaskReportRecord} IndividualTaskReportRecord
 * @typedef {import("@tanstack/react-table").ColumnHelper<IndividualTaskReportRecord>} IndividualReportTableColumnHelper
 */

/**
 * @param {import("./IndividualReportTable.columns.types").IndividualReportTableColumnsParams} params
 */
export function buildIndividualReportTableColumns(params) {
  const {
    setContextMenuTarget,
    isHoldingModifierDown,
    schemaRules,
    teamMemberWorkAnniversaryDate,
    userIsAdmin,
    updatingTask,
    contextMenuRef,
    clientFilterTemplate,
    moduleFilterTemplate,
    categoryFilterTemplate,
    taskNameFilterTemplate,
    projects,
    totals,
  } = params

  /**
   * @param {import("./IndividualReportTable.controller.types").IndividualReportTableType} table
   * @param {string} value
   */
  function onLeftClickClientCode(table, value) {
    const column = table.getColumn("projectCode")
    if (!column) return

    const current = column.getFilterValue()
    const values = Array.isArray(current) ? current : []

    const projectsNames = Array.from(
      new Set(
        projects.filter((c) => c.code === value)?.[0]?.items?.map((p) => p.name)
      )
    )

    const hasEveryoneSelected = projectsNames.every((name) =>
      values.includes(name)
    )

    if (hasEveryoneSelected) {
      column.setFilterValue(values.filter((v) => !projectsNames.includes(v)))
    } else {
      column.setFilterValue([...values, ...projectsNames])
    }
  }

  const {
    DateCellTemplate,
    GroupedDateCellTemplate,
    ProjectCodeCellTemplate,
    ModuleCellTemplate,
    CategoryCellTemplate,
    SisuTimeFooterTemplate,
    BilledTimeCellTemplate,
    BilledTimeFooterTemplate,
    TimeDifferenceCellTemplate,
    TimeDifferenceFooterTemplate,
    ActionsCellTemplate,
    TaskCellTemplate,
    SisuTimeCellTemplate,
  } = buildIndividualReportTableCells({
    setContextMenuTarget,
    isHoldingModifierDown,
    onLeftClickClientCode,
    schemaRules,
    teamMemberWorkAnniversaryDate,
    userIsAdmin,
    updatingTask,
    contextMenuRef,
    totals,
  })

  /**
   * @param {import("@tanstack/react-table").Row<IndividualTaskReportRecord>} rowA
   * @param {import("@tanstack/react-table").Row<IndividualTaskReportRecord>} rowB
   * @param {string} columnId
   */
  function sortByBillability(rowA, rowB, columnId) {
    const a = rowA.getValue(columnId)
    const b = rowB.getValue(columnId)

    if (a === b) {
      const aBillable =
        isTaskReportItem(rowA.original) && rowA.original.clientIsBillable
      const bBillable =
        isTaskReportItem(rowB.original) && rowB.original.clientIsBillable

      if (aBillable === bBillable) return 0
      return aBillable ? 1 : -1
    }

    return a - b
  }

  /**
   * @param {string} columnId
   * @param {import("@tanstack/react-table").Row<IndividualTaskReportRecord>[]} leafRows
   */
  function sumBillable(columnId, leafRows) {
    const billableRows = leafRows.filter(
      (row) => isTaskReportItem(row.original) && row.original.clientIsBillable
    )

    if (billableRows.length === 0) {
      return null
    }

    return billableRows.reduce((sum, row) => {
      const value = row.getValue(columnId)
      return sum + (typeof value === "number" ? value : 0)
    }, 0)
  }

  /** @type {import("../../../../factories/reports/createTaskReportsCommonColumns.factory.types").TaskReportCommonColumnConfig<IndividualTaskReportRecord>} */
  const config = {
    date: {
      cellTemplate: DateCellTemplate,
    },
    projectCode: {
      cellTemplate: ProjectCodeCellTemplate,
      filterElement: clientFilterTemplate,
      filterFn: (row, _columnId, filterValue) =>
        row.original.isPlaceholder ?
          false
        : tableFilterValueIncludes(row.original.projectName, filterValue),
    },
    module: {
      cellTemplate: ModuleCellTemplate,
      filterElement: moduleFilterTemplate,
      filterFn: (row, _columnId, filterValue) =>
        row.original.isPlaceholder ?
          false
        : tableFilterValueIncludes(row.original.moduleId, filterValue),
    },
    categoryTitle: {
      cellTemplate: CategoryCellTemplate,
      filterElement: categoryFilterTemplate,
      filterFn: (row, _columnId, filterValue) => {
        if (row.original.isPlaceholder) {
          return false
        }

        const { categoryId, specialCategoryId } = row.original
        return tableFilterValueIncludes(
          [`normal:${categoryId}`, `special:${specialCategoryId}`],
          filterValue
        )
      },
    },
    task: {
      cellTemplate: TaskCellTemplate,
      filterElement: taskNameFilterTemplate,
    },
    sisuTime: {
      cellTemplate: SisuTimeCellTemplate,
      footerTemplate: SisuTimeFooterTemplate,
    },
    billedTime: {
      cellTemplate: BilledTimeCellTemplate,
      footerTemplate: BilledTimeFooterTemplate,
      sortingFn: sortByBillability,
      aggregationFn: sumBillable,
    },
    timeDifference: {
      cellTemplate: TimeDifferenceCellTemplate,
      footerTemplate: TimeDifferenceFooterTemplate,
      sortingFn: sortByBillability,
      aggregationFn: sumBillable,
    },
    actions: {
      cellTemplate: ActionsCellTemplate,
    },
  }

  /** @type {IndividualReportTableColumnHelper} */
  const columnHelper = createColumnHelper()

  const {
    date,
    projectCode,
    module,
    categoryTitle,
    task,
    sisuTime,
    billedTime,
    timeDifference,
    actions,
  } = createTaskReportsCommonColumns({
    columnHelper,
    config,
    groupedDateCellTemplate: GroupedDateCellTemplate,
  })

  const columns = [
    columnHelper.display({
      id: "select",
      header: SelectHeaderTemplate,
      cell: SelectCellTemplate,
      size: 38,
      meta: {
        className: "select-column",
      },
    }),

    date,
    projectCode,
    module,
    categoryTitle,
    task,
    sisuTime,
    billedTime,
    timeDifference,
    actions,
  ]

  return columns
}
